import { Container } from "react-bootstrap";
import styled from "styled-components";

import { colors, types } from "../../../libs";

export const PlanDetailsContainer = styled(Container)`
  padding: 0;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;

  @media screen and (max-width: 575px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const Subtitle = styled.h3`
  ${types.body.title};
  background: ${colors.text.textGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

export const CreateButtonWrapper = styled.div`
  width: 150px;

  @media screen and (max-width: 575px) {
    width: 100%;
  }
`;

export const EmptyState = styled.div`
  ${types.body.normal};
  color: ${colors.text.gray};
  border: 1px solid ${colors.background.lightWhite};
  border-radius: 8px;
  margin-top: 24px;
  padding: 32px 20px;
  text-align: center;
`;

export const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 24px 0 12px;

  @media screen and (max-width: 575px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const SearchWrapper = styled.div`
  position: relative;
  width: min(360px, 100%);
`;

export const SearchIcon = styled.span`
  color: ${colors.text.gray};
  left: 14px;
  line-height: 0;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

export const SearchInput = styled.input`
  ${types.body.normal};
  background: ${colors.background.formField};
  border: 1px solid ${colors.background.lightWhite};
  border-radius: 20px;
  color: ${colors.text.white};
  padding: 9px 16px 9px 42px;
  width: 100%;

  &::placeholder {
    color: ${colors.text.lightGray};
  }

  &:focus {
    border-color: ${colors.text.green};
    outline: none;
  }

  &::-webkit-search-cancel-button {
    filter: invert(1);
  }
`;

export const ToolbarControls = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;

  @media screen and (max-width: 575px) {
    justify-content: space-between;
  }
`;

export const FilterSelect = styled.select`
  ${types.body.normal};
  background: ${colors.background.formField};
  border: 1px solid ${colors.background.lightWhite};
  border-radius: 20px;
  color: ${colors.text.white};
  cursor: pointer;
  padding: 8px 34px 8px 14px;

  &:focus {
    border-color: ${colors.text.green};
    outline: none;
  }
`;

export const ResultCount = styled.span`
  ${types.body.small};
  color: ${colors.text.gray};
  white-space: nowrap;
`;

export const TableContainer = styled.div`
  border: 1px solid ${colors.background.lightWhite};
  border-radius: 8px;
  overflow-x: auto;
`;

export const PlansTable = styled.table`
  background: ${colors.background.black};
  border-collapse: collapse;
  color: ${colors.text.mediumWhite};
  min-width: 1000px;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid ${colors.background.lightWhite};
    padding: 14px 16px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    ${types.headers.itemSmall};
    background: ${colors.background.lightBlack};
    color: ${colors.text.white};
    white-space: nowrap;
  }

  td {
    ${types.body.normal};
  }

  th:first-child,
  td:first-child {
    min-width: 230px;
    width: 26%;
  }

  th:nth-child(3),
  td:nth-child(3) {
    min-width: 240px;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

export const SortButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  display: inline-flex;
  font: inherit;
  gap: 7px;
  padding: 0;

  &:hover {
    color: ${colors.text.green};
  }

  &:focus-visible {
    outline: 2px solid ${colors.text.green};
    outline-offset: 4px;
  }
`;

export const SortIndicator = styled.span<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? colors.text.green : colors.text.lightGray)};
  font-size: 10px;
`;

export const PlanRow = styled.tr<{ $muted: boolean }>`
  background: ${colors.background.black};
  transition: background-color 0.15s ease;

  td {
    color: ${({ $muted }) => ($muted ? colors.text.disabled : colors.text.mediumWhite)};
  }

  &:hover {
    background: ${colors.background.lightBlack};
  }
`;

export const PlanName = styled.div<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.text.disabled : colors.text.white)};
  font-size: 16px;
  font-weight: 600;
`;

export const PlanDescription = styled.div`
  color: ${colors.text.gray};
  display: -webkit-box;
  line-height: 1.4;
  margin-top: 4px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const Badge = styled.span<{
  $variant: "success" | "neutral" | "danger";
}>`
  background: ${({ $variant }) =>
    $variant === "success"
      ? "rgba(183, 248, 77, 0.12)"
      : $variant === "danger"
        ? colors.background.error
        : colors.background.lightWhite};
  border-radius: 999px;
  color: ${({ $variant }) =>
    $variant === "success"
      ? colors.text.green
      : $variant === "danger"
        ? colors.text.error
        : colors.text.mediumWhite};
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  white-space: nowrap;
`;

export const PriceCell = styled.td`
  color: ${colors.text.white};
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  white-space: nowrap;
`;

export const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

export const ActionButton = styled.button`
  background: transparent;
  border: 0;
  color: ${colors.text.green};
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: ${colors.text.lightGreen};
  }

  &:focus-visible {
    outline: 2px solid ${colors.text.green};
    outline-offset: 3px;
  }
`;

export const NoResultsCell = styled.td`
  color: ${colors.text.gray};
  padding: 32px 20px !important;
  text-align: center !important;
`;
